import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  courierKeys,
  mapCourierError,
  unassignOrdersFromCourier,
  useTranslation,
} from '@dukanti/shared';
import type { CourierUnassignmentResult, TranslationKey } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export interface UnassignOrdersFromCourierVariables {
  readonly orderIds: string[];
}

export function useUnassignOrdersFromCourier(
  storeId: string,
): UseMutationResult<CourierUnassignmentResult, unknown, UnassignOrdersFromCourierVariables> {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<CourierUnassignmentResult, unknown, UnassignOrdersFromCourierVariables>({
    mutationFn: ({ orderIds }) =>
      unassignOrdersFromCourier(supabase, {
        storeId,
        orderIds,
      }),
    onSuccess: (result, variables) => {
      if (!result.success) {
        toast.error(t(mapCourierError(undefined, result) as TranslationKey));
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ['store-orders', storeId] });
      void queryClient.invalidateQueries({ queryKey: courierKeys.batches(storeId) });
      for (const orderId of variables.orderIds) {
        void queryClient.invalidateQueries({ queryKey: ['admin-order-detail', orderId] });
      }
      toast.success(t('admin.orders.courierAssignment.unassigned' as TranslationKey));
    },
    onError: (err) => {
      toast.error(t(mapCourierError(err) as TranslationKey));
    },
  });
}
